import { Component, ChangeDetectionStrategy, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { IconComponent } from '@shared/components/icon/icon.component';
import { IconName } from '@shared/components/icon/icon.component';

export interface ToastData {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration: number;
}

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToastComponent implements OnInit, OnDestroy {
  protected readonly data = inject<ToastData>(MAT_SNACK_BAR_DATA);
  private readonly snackBarRef = inject(MatSnackBarRef<ToastComponent>);

  protected readonly progress = signal(100);
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly tickMs = 20;

  get typeIcon(): IconName {
    switch (this.data.type) {
      case 'success':
        return 'check-circle';
      case 'error':
        return 'x-circle';
      case 'warning':
        return 'alert-circle';
      default:
        return 'info';
    }
  }

  ngOnInit(): void {
    const steps = this.data.duration / this.tickMs;
    const decrement = 100 / steps;

    this.intervalId = setInterval(() => {
      const next = this.progress() - decrement;
      if (next <= 0) {
        this.progress.set(0);
        this.clearTimer();
      } else {
        this.progress.set(next);
      }
    }, this.tickMs);
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  protected dismiss(): void {
    this.snackBarRef.dismiss();
  }

  private clearTimer(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
